
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { promoterStoreActions, promoterStoreGetters } from '@/store/promoter.store';
import Promoter from '@/models/Promoter';
import moment from 'moment';
import PromoterPayout from '@/models/PromoterPayout';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ROUTE_SHARE_MANAGEMENT } from '@/router/routes';

const PromoterStore = namespace('promoter');

@Component({
    components: {
        PromoterTableComponent: () => import(
            /* webpackChunkName: "PromoterTableComponent" */
            '@/components/Promoters/PromotersTable.component.vue'),
        EditPromoterComponent: () => import(
            /* webpackChunkName: "EditPromoterComponent" */
            '@/components/Promoters/EditPromoter.component.vue'
        ),
        PromoterStatisticsListComponent: () => import(
            /* webpackChunkName: "PromoterStatisticsListComponent" */
            '@/components/Promoters/PromoterStatisticsList.component.vue'
        )
    }
})
export default class PromoterView extends Vue {
    @PromoterStore.Action(promoterStoreActions.GET)
    public loadAllPromoters!: (payload: { from: string, to: string }) => Promise<Promoter[]>;

    @PromoterStore.Action(promoterStoreActions.DELETE)
    public deletePromoter!: (id: string) => Promise<void>;

    @PromoterStore.Action(promoterStoreActions.PAYOUTS)
    public getPayoutsAction!: (payload: { from: string, to: string }) => Promise<PromoterPayout[]>;

    @PromoterStore.Action(promoterStoreActions.MARK_PAID)
    public markPaidAction!: (payload: {
        id: string, amount: number, period: string, from: string, to: string
    }) => Promise<PromoterPayout>;

    @PromoterStore.Getter(promoterStoreGetters.PROMOTERS)
    public promoters!: Promoter[];

    public activeTab: number = 0;

    public startMenu: boolean = false;
    public endMenu: boolean = false;
    public startDate: string | null = null;
    public endDate: string | null = null;

    public searchValue: string = '';
    public showCreationDialog: boolean = false;
    public showDeleteDialog: boolean = false;
    public showStatusDialog: boolean = false;
    public showStatisticsDialog: boolean = false;
    public showMarkPaidDialog: boolean = false;

    private isLoading: boolean = false;
    private isLoadingPayouts: boolean = false;
    private isSubmittingMarkPaid: boolean = false;
    private selectedPromoter: Promoter | null = null;

    private payouts: PromoterPayout[] = [];
    private selectedPayout: PromoterPayout | null = null;
    private markPaidAmount: string = '';
    private markPaidPeriod: string = '';

    public payoutHeaders = [
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.PROMOTER'), value: 'promoterName', sortable: true },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.REFERRED_DRIVERS'), value: 'referredDrivers', sortable: true },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.ACTIVATED_DRIVERS'), value: 'activatedDrivers', sortable: true },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.SIGNUP_BONUS'), value: 'signupBonusTotal', sortable: true },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.COMMISSION'), value: 'commissionTotal', sortable: true },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.AMOUNT_DUE'), value: 'amountDue', sortable: true },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.STATUS'), value: 'status', sortable: false },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.PAYMENTS'), value: 'paymentHistory', sortable: false },
        { text: this.$t('PROMOTER.PAYOUTS.TABLE.ACTIONS'), value: 'actions', align: 'end', sortable: false }
    ];

    public async created() {
        const today = moment(new Date());
        this.endDate = today.format('yyyy-MM-DD');
        today.set({ date: 1 });
        this.startDate = today.format('yyyy-MM-DD');
        this.markPaidPeriod = moment(new Date()).format('YYYY-MM');

        try {
            this.isLoading = true;
            await this.loadAllPromoters({ from: this.startDate, to: this.endDate });
        } finally {
            this.isLoading = false;
        }
    }

    public async onTabChanged(tab: number) {
        if (tab === 1 && this.payouts.length === 0) {
            await this.loadPayouts();
        }
    }

    private async loadPromoters() {
        await this.loadAllPromoters({ from: this.startDate!, to: this.endDate! });
    }

    private async loadPayouts() {
        try {
            this.isLoadingPayouts = true;
            this.payouts = await this.getPayoutsAction({ from: this.startDate!, to: this.endDate! });
        } catch (e) {
            this.$notifyErrorSimplified('PROMOTER.PAYOUTS.ERROR_LOAD');
            this.payouts = [];
        } finally {
            this.isLoadingPayouts = false;
        }
    }

    public async closeDialog(reload: boolean) {
        this.showCreationDialog = false;
        this.showDeleteDialog = false;
        this.showStatusDialog = false;
        this.showStatisticsDialog = false;
        this.selectedPromoter = null;
        if (reload) {
            await this.loadPromoters();
            if (this.activeTab === 1) {
                await this.loadPayouts();
            }
        }
    }

    public async onStatisticsClicked(item: Promoter) {
        this.selectedPromoter = item;
        this.showStatisticsDialog = true;
    }

    public async onEditPromoter(item: Promoter) {
        this.selectedPromoter = item;
        this.showCreationDialog = true;
    }

    public onDeletePromoter(item: Promoter) {
        this.selectedPromoter = item;
        this.showDeleteDialog = true;
    }

    public async onDeletePromoterConfirmed() {
        if (!this.selectedPromoter) return;
        try {
            this.isLoading = true;
            await this.deletePromoter(this.selectedPromoter.id);
            this.showDeleteDialog = false;
            await this.loadPromoters();
            this.$notifySuccessSimplified('PROMOTER.DELETE.SUCCESS');
        } catch (e) {
            this.$notifyErrorSimplified('PROMOTER.DELETE.ERROR');
        } finally {
            this.isLoading = false;
        }
    }

    private get payoutRows() {
        return this.payouts.map((payout) => ({
            payout,
            promoterId: payout.promoterId,
            promoterName: payout.promoterName,
            referredDrivers: payout.referredDrivers,
            referredCustomers: payout.referredCustomers,
            activatedDrivers: payout.commissionRides > 0 ? payout.referredDrivers : 0,
            signupBonusTotal: payout.signupBonusTotal,
            commissionTotal: payout.commissionTotal,
            commissionRides: payout.commissionRides,
            amountDue: payout.amountDue,
            status: payout.isPaid
                ? this.$t('PROMOTER.PAYOUTS.STATUS_PAID', { count: payout.payments.length }).toString()
                : this.$t('PROMOTER.PAYOUTS.STATUS_UNPAID').toString(),
            paymentHistory: payout.payments.length > 0
                ? payout.payments.map((payment) => `${payment.period}: ${this.toAmount(payment.amount)}`).join(', ')
                : '-'
        }));
    }

    private get selectedPayoutName() {
        return this.selectedPayout?.promoterName ?? '';
    }

    private openMarkPaidDialog(row: any) {
        this.selectedPayout = row.payout;
        this.markPaidAmount = row.amountDue?.toString() ?? '';
        this.markPaidPeriod = this.markPaidPeriod || moment(new Date()).format('YYYY-MM');
        this.showMarkPaidDialog = true;
    }

    private closeMarkPaidDialog() {
        this.showMarkPaidDialog = false;
        this.selectedPayout = null;
        this.markPaidAmount = '';
    }

    private async markPaidConfirmed() {
        const amount = parseFloat(this.markPaidAmount);
        if (!this.selectedPayout || !this.selectedPayout.promoterId || Number.isNaN(amount) || amount <= 0 || !this.markPaidPeriod) {
            this.$notifyErrorSimplified('PROMOTER.PAYOUTS.ERROR_MARK_PAID');
            return;
        }
        try {
            this.isSubmittingMarkPaid = true;
            const updatedPayout = await this.markPaidAction({
                id: this.selectedPayout.promoterId,
                amount,
                period: this.markPaidPeriod,
                from: this.startDate!,
                to: this.endDate!
            });
            this.payouts = this.payouts.map((payout) => payout.promoterId === updatedPayout.promoterId
                ? updatedPayout
                : payout);
            this.$notifySuccessSimplified('PROMOTER.PAYOUTS.SUCCESS_MARK_PAID');
            this.closeMarkPaidDialog();
        } catch (e) {
            this.$notifyErrorSimplified('PROMOTER.PAYOUTS.ERROR_MARK_PAID');
        } finally {
            this.isSubmittingMarkPaid = false;
        }
    }

    private downloadPayoutsPdf() {
        const JsPdfConstructor: any = jsPDF;
        const doc = new JsPdfConstructor();
        const today = moment(new Date()).format('YYYY-MM-DD');
        const head = [[
            this.$t('PROMOTER.PAYOUTS.TABLE.PROMOTER').toString(),
            this.$t('PROMOTER.PAYOUTS.TABLE.REFERRED_DRIVERS').toString(),
            this.$t('PROMOTER.PAYOUTS.TABLE.REFERRED_CUSTOMERS').toString(),
            this.$t('PROMOTER.PAYOUTS.TABLE.AMOUNT_DUE').toString(),
            this.$t('PROMOTER.PAYOUTS.TABLE.STATUS').toString()
        ]];
        const body = this.payoutRows.map((row) => [
            row.promoterName,
            row.referredDrivers,
            row.referredCustomers,
            this.toAmount(row.amountDue),
            row.status
        ]);

        doc.setFontSize(14);
        doc.text(this.$t('PROMOTER.PAYOUTS.PDF_TITLE').toString(), 14, 16);
        autoTable(doc, {
            head,
            body,
            startY: 22
        });
        doc.save(`promoter-payouts-${today}.pdf`);
    }

    private goToShareManagement() {
        this.$router.push({ name: ROUTE_SHARE_MANAGEMENT });
    }

    private toAmount(amount: number): string {
        return `${(amount ?? 0).toFixed(2)} NAD`;
    }
}
